import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Search, Unlock, Gift, Zap, Trophy, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const steps = [
  {
    icon: Search,
    title: 'Browse Matches',
    description: 'Explore upcoming football matches from top leagues worldwide. Use the date selector to find games.',
    color: 'bg-primary/10 text-primary',
  },
  {
    icon: Unlock,
    title: 'Unlock Predictions',
    description: 'Each prediction costs 1 credit. Get expert AI analysis, betting tips, and win probabilities.',
    color: 'bg-success/10 text-success',
  },
  {
    icon: Gift,
    title: 'Earn Free Credits',
    description: 'Sign up for 10 free credits, claim 3 daily rewards, and invite friends for bonus credits.',
    color: 'bg-amber-500/10 text-amber-500',
  },
  {
    icon: Zap,
    title: 'Live Updates',
    description: 'For live matches, predictions update in real-time based on current match events.',
    color: 'bg-live/10 text-live',
  },
];

const features = [
  { icon: Trophy, label: 'Match Winner Tips' },
  { icon: TrendingUp, label: 'Over/Under Goals' },
  { icon: CheckCircle2, label: 'Both Teams to Score' },
];

export function HowItWorksModal({ open, onOpenChange }: HowItWorksModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <Trophy className="h-7 w-7 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-bold">
            How WinWGo Works
          </DialogTitle>
          <DialogDescription className="text-base">
            Get expert AI-powered football predictions in 4 simple steps
          </DialogDescription>
        </DialogHeader>

        {/* Steps */}
        <div className="space-y-4 py-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border/50"
            >
              <div className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                step.color
              )}>
                <step.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    Step {index + 1}
                  </span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Prediction types */}
        <div className="py-4 border-t border-border/50">
          <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3">
            What you get with each prediction
          </h4>
          <div className="flex flex-wrap gap-2">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/50"
              >
                <feature.icon className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="pt-4 border-t border-border/50">
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full h-12 text-base font-semibold gap-2"
          >
            Start Exploring Matches
            <ArrowRight className="h-5 w-5" />
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-3">
            Data sourced from real API-Football statistics
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
